import React, {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {openDrawer} from 'src/modules/entitlements/components/drawer/entitlement-drawer.slice';
import {EntitlementType} from 'src/modules/entitlements/components/drawer/typedef';

import Button from '@pb/kit-component-button';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';

import type {EntitlementsNavigationState} from '../utils/getEntitlementsReturnPath';

type Props = {
    navigationState?: EntitlementsNavigationState,
};

export const ManageEntitlementsHeaderButtons = ({navigationState}: Props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {hasAccess} = useAdminAccessRight();

    const navigateToResourceCreation = useCallback(() => {
        navigate('/entitlements/create-resource', {state: navigationState});
    }, [navigate, navigationState]);

    const navigateToGrantCreation = useCallback(() => {
        navigate('/entitlements/create-grant', {state: navigationState});
    }, [navigate, navigationState]);

    const navigateToRoleCreation = useCallback(() => {
        navigate('/entitlements/create-role', {state: navigationState});
    }, [navigate, navigationState]);

    const navigateToPresetCreation = useCallback(() => {
        navigate('/entitlements/create-preset');
    }, [navigate]);

    const openNotAssociateEntitlementsDrawer = useCallback(() => {
        dispatch(openDrawer({
            entitlementType: EntitlementType.NOT_ASSOCIATED,
            entitlementId: '',
            notAssociatedEntitlementsDetails: {
                activeTab: 'roles',
                pagination: {currentPage: 1, pageSize: 10},
            },
        }));
    }, [dispatch]);

    return (
        <Group>
            {hasAccess(ACCESS_RIGHTS.DELETE_ENTITLEMENTS) ? (
                <Button variant="Type.SECONDARY" onClick={openNotAssociateEntitlementsDrawer} colorVariant="ERROR">
                    Delete
                </Button>
            ) : null}
            <DropdownMenu
                horizontalOffset={17}
                verticalOffset={10}
                anchor={({getAnchorProps, anchorRef, positionRef}) => (
                    <Button
                        ref={anchorRef}
                        {...getAnchorProps()}
                        variant="Type.TERTIARY"
                        endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}>
                        Create
                    </Button>
                )}>
                <DropdownMenuItem
                    caption="Resource"
                    onClick={navigateToResourceCreation}
                />
                <DropdownMenuItem
                    caption="Grant"
                    onClick={navigateToGrantCreation}
                />
                <DropdownMenuItem
                    caption="Role"
                    onClick={navigateToRoleCreation}
                />
                {hasAccess(ACCESS_RIGHTS.MANAGE_ENTITLEMENT_PRESET) ? (
                    <DropdownMenuItem
                        caption="Preset"
                        onClick={navigateToPresetCreation}
                    />
                ) : null}
            </DropdownMenu>
        </Group>
    );
};
